import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ui/ErrorBoundary";
import NoEncontrado from "components/ui/NoEncontrado";
import LoadingOverlay from "components/ui/LoadingOverlay";
import LoginScreen from './pages/login-screen';
import RequireAcceso from 'components/RequireAcceso';
import AppLayout from 'components/AppLayout';
import { useAuth } from 'contexts/AuthContext';

// Lazy-load heavy routes (reportes ships recharts/d3/jspdf/html2canvas).
// Login stays eager because it's the entry point.
const VisitFormRegistrationScreen = lazy(() => import('./pages/visit-form-registration-screen'));
const VisitRecordsViewerScreen = lazy(() => import('./pages/visit-records-viewer-screen'));
const ClientsDirectoryScreen = lazy(() => import('./pages/clients-directory-screen'));
const ReportesScreen = lazy(() => import('./pages/reportes-screen'));

const RouteFallback = () => <LoadingOverlay isVisible message="Cargando..." fullScreen />;

// ErrorBoundary y 404 de shared/vls-ui (auditoría de UI 2026-10-20, arreglo
// #10): en español, con Reintentar y Volver al hub. Al cambiar de ruta el
// error se limpia solo (resetKeys). "Volver al inicio" va a "/", que manda a
// la pantalla de entrada o, con sesión, a la de siempre.
function Protegido({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKeys={[pathname]}>{children}</ErrorBoundary>;
}

// Con sesión y acceso a Visitas, el 404 va dentro del AppLayout para conservar
// el encabezado y el menú (convención 2026-10-20: el AppHeader en todas las
// pantallas). Sin sesión sigue saliendo solo, como antes: no cambia a dónde
// manda ni lo que ve quien no entró.
function NoEncontradoRuta() {
  const navigate = useNavigate();
  const { isAuthenticated, userRole } = useAuth();
  const aviso = <NoEncontrado onInicio={() => navigate('/')} />;
  return isAuthenticated && userRole ? <AppLayout>{aviso}</AppLayout> : aviso;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <Protegido>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <RouterRoutes>
            <Route path="/" element={<LoginScreen />} />
            <Route path="/login" element={<LoginScreen />} />
            {/* Todo lo de adentro pasa por el guardián: sesión Y membresía de
                "visitas". Antes estas rutas estaban sueltas y cada pantalla se
                cuidaba sola, menos clients-directory, que no se cuidaba. */}
            <Route path="/visit-form-registration-screen" element={<RequireAcceso><VisitFormRegistrationScreen /></RequireAcceso>} />
            <Route path="/visit-records-viewer-screen" element={<RequireAcceso><VisitRecordsViewerScreen /></RequireAcceso>} />
            <Route path="/clients-directory-screen" element={<RequireAcceso><ClientsDirectoryScreen /></RequireAcceso>} />
            <Route path="/reportes-screen" element={<RequireAcceso><ReportesScreen /></RequireAcceso>} />
            <Route path="*" element={<NoEncontradoRuta />} />
          </RouterRoutes>
        </Suspense>
      </Protegido>
    </BrowserRouter>
  );
};

export default Routes;
